import React, { useState, useEffect, useRef, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useStore } from '../../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../components/ui/select';
import {
  CircleNotch, MapPin, Users, Storefront, Buildings, User,
  Envelope, Phone, Hash, Receipt, CalendarBlank, CaretRight,
  ArrowsOut, X, Package
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DeliveryMapPage = () => {
  const { currentStore } = useStore();
  const mapContainer = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  const [mapData, setMapData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [customerProfile, setCustomerProfile] = useState(null);
  const [savingProfile, setSavingProfile] = useState(false);
  const [filterType, setFilterType] = useState('all');
  const [trends, setTrends] = useState(null);
  const [loadingTrends, setLoadingTrends] = useState(false);

  const fetchMapData = useCallback(async () => {
    if (!currentStore) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/stores/${currentStore.id}/customers/map`, { withCredentials: true });
      setMapData(res.data);
    } catch (err) {
      console.error('Map data error:', err);
      toast.error('Erreur chargement carte');
    } finally {
      setLoading(false);
    }
  }, [currentStore]);

  useEffect(() => { fetchMapData(); }, [fetchMapData]);

  // Initialize map (re-runs when the container becomes available)
  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;
    try {
      const map = L.map(mapContainer.current, {
        center: [43.60, 4.80], // Istres approx
        zoom: 9,
        scrollWheelZoom: true,
        zoomControl: true,
      });
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://openstreetmap.org">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(map);
      mapRef.current = map;
      // Ensure Leaflet recalculates size once its container is fully rendered
      setTimeout(() => { try { map.invalidateSize(); } catch (e) { /* noop */ } }, 100);
      return () => { map.remove(); mapRef.current = null; };
    } catch (err) {
      console.error('[DeliveryMap] Failed to init leaflet:', err);
    }
  }, [currentStore]);

  // Update markers when data or filter changes
  useEffect(() => {
    if (!mapRef.current || !mapData) return;

    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    const filtered = mapData.markers.filter(m => {
      if (filterType === 'pro') return m.client_type === 'pro';
      if (filterType === 'particulier') return m.client_type === 'particulier';
      return true;
    });

    filtered.forEach(customer => {
      const isPro = customer.client_type === 'pro';
      const iconHtml = `
        <div style="
          width:38px;height:38px;border-radius:50%;cursor:pointer;
          background:${isPro ? '#0052FF' : '#c2185b'};
          border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,0.25);
          display:flex;align-items:center;justify-content:center;
          font-weight:700;font-size:13px;color:#fff;font-family:'Jost',sans-serif;
          transition:transform 0.2s;
        ">${isPro ? 'P' : 'M'}</div>
      `;
      const icon = L.divIcon({
        html: iconHtml,
        className: 'magere-marker',
        iconSize: [38, 38],
        iconAnchor: [19, 19],
      });
      const marker = L.marker([customer.lat, customer.lng], { icon, title: customer.name || customer.email })
        .addTo(mapRef.current);
      marker.on('click', () => {
        setSelectedCustomer(customer);
        fetchTrends(customer.email);
      });
      markersRef.current.push(marker);
    });

    if (filtered.length > 0) {
      const bounds = L.latLngBounds(filtered.map(m => [m.lat, m.lng]));
      mapRef.current.fitBounds(bounds, { padding: [60, 60], maxZoom: 12 });
    }
  }, [mapData, filterType]);

  const fetchTrends = async (email) => {
    setTrends(null);
    setLoadingTrends(true);
    try {
      const res = await axios.get(`${API_URL}/api/stores/${currentStore.id}/customers/${encodeURIComponent(email)}/trends`, { withCredentials: true });
      setTrends(res.data);
    } catch { setTrends(null); }
    finally { setLoadingTrends(false); }
  };

  const formatFrequency = (days) => {
    if (!days || days <= 0) return 'Premiere commande';
    if (days <= 7) return `~${days}j (hebdo)`;
    if (days <= 14) return `~${days}j (bi-hebdo)`;
    if (days <= 35) return `~${Math.round(days / 7)} sem.`;
    return `~${Math.round(days / 30)} mois`;
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: '2-digit' });
    } catch { return dateStr.slice(0, 10); }
  };


  const openProfile = async (email) => {
    try {
      const res = await axios.get(`${API_URL}/api/stores/${currentStore.id}/customers/${encodeURIComponent(email)}/profile`, { withCredentials: true });
      setCustomerProfile(res.data);
      setProfileDialogOpen(true);
    } catch {
      setCustomerProfile({
        email, client_type: 'particulier', company_name: '', siret: '', tva_intra: '',
        billing_address: { address: '', city: '', postal_code: '', country: 'FR' }
      });
      setProfileDialogOpen(true);
    }
  };

  const saveProfile = async () => {
    if (!customerProfile) return;
    setSavingProfile(true);
    try {
      await axios.put(
        `${API_URL}/api/stores/${currentStore.id}/customers/${encodeURIComponent(customerProfile.email)}/profile`,
        customerProfile,
        { withCredentials: true }
      );
      toast.success('Profil mis a jour');
      setProfileDialogOpen(false);
      fetchMapData(); // Refresh markers
    } catch { toast.error('Erreur'); }
    finally { setSavingProfile(false); }
  };

  if (!currentStore) {
    return <div className="py-12 text-center text-slate-500">Selectionnez une boutique</div>;
  }

  return (
    <div className="space-y-4" data-testid="delivery-map-page">
      <div>
        <h1 className="font-cabinet text-3xl font-bold tracking-tight">Carte des clients</h1>
        <p className="text-slate-500 mt-1">Visualisez vos clients par zone de livraison</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">

        {/* ── Left sidebar: stats ── */}
        <div className="space-y-4">
          {/* Summary cards */}
          <div className="grid grid-cols-3 lg:grid-cols-1 gap-3">
            <Card className="dashboard-card">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center">
                  <Users size={20} className="text-slate-600" weight="fill" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{mapData?.total_customers || 0}</p>
                  <p className="text-xs text-slate-500">Total clients</p>
                </div>
              </CardContent>
            </Card>
            <Card className="dashboard-card">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                  <Buildings size={20} className="text-blue-600" weight="fill" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{mapData?.total_pro || 0}</p>
                  <p className="text-xs text-slate-500">Professionnels</p>
                </div>
              </CardContent>
            </Card>
            <Card className="dashboard-card">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center">
                  <User size={20} className="text-rose-600" weight="fill" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{mapData?.total_particulier || 0}</p>
                  <p className="text-xs text-slate-500">Particuliers</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filter */}
          <Card className="dashboard-card">
            <CardContent className="p-3">
              <Label className="text-xs text-slate-500 mb-2 block">Filtrer par type</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger data-testid="filter-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les clients</SelectItem>
                  <SelectItem value="pro">Professionnels</SelectItem>
                  <SelectItem value="particulier">Particuliers</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Zone stats */}
          <Card className="dashboard-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Clients par zone</CardTitle>
            </CardHeader>
            <CardContent className="p-3 space-y-2">
              {loading ? (
                <div className="py-4 text-center"><CircleNotch className="animate-spin mx-auto" size={20} /></div>
              ) : mapData?.zone_stats?.length > 0 ? (
                mapData.zone_stats.map(zone => (
                  <div key={zone.id} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-2 min-w-0">
                      <MapPin size={14} className={zone.id === 'hors_zone' ? 'text-slate-400' : 'text-blue-500'} weight="fill" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{zone.name}</p>
                        <div className="flex gap-2 text-[10px] text-slate-400">
                          {zone.pro > 0 && <span className="text-blue-500">{zone.pro} pro</span>}
                          {zone.particulier > 0 && <span className="text-rose-500">{zone.particulier} part.</span>}
                        </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2">
                      <p className="text-sm font-bold">{zone.count}</p>
                      <p className="text-[10px] text-slate-400">{zone.total_spent.toFixed(0)} EUR</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-xs text-slate-400 text-center py-4">Aucune donnee</p>
              )}
            </CardContent>
          </Card>

          {/* Legend */}
          <Card className="dashboard-card">
            <CardContent className="p-3 space-y-2">
              <p className="text-xs text-slate-500 font-medium">Legende</p>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-[#0052FF] border-2 border-white shadow flex items-center justify-center text-[9px] text-white font-bold">P</div>
                <span className="text-xs text-slate-600">Client professionnel</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-full bg-[#c2185b] border-2 border-white shadow flex items-center justify-center text-[9px] text-white font-bold">M</div>
                <span className="text-xs text-slate-600">Client particulier</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* ── Map + selected customer ── */}
        <div className="lg:col-span-3 space-y-4">
          <div className="relative rounded-xl overflow-hidden border shadow-sm" style={{ height: '520px' }}>
            {loading && (
              <div className="absolute inset-0 bg-white/80 z-10 flex items-center justify-center">
                <CircleNotch size={32} className="animate-spin text-slate-400" />
              </div>
            )}
            <div ref={mapContainer} style={{ width: '100%', height: '100%' }} data-testid="mapbox-container" />
          </div>

          {/* Selected customer card */}
          {selectedCustomer && (
            <Card className="dashboard-card border-l-4" style={{ borderLeftColor: selectedCustomer.client_type === 'pro' ? '#0052FF' : '#c2185b' }} data-testid="customer-card">
              <CardContent className="p-5">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold shadow-lg ${selectedCustomer.client_type === 'pro' ? 'bg-[#0052FF]' : 'bg-[#c2185b]'}`}>
                      {selectedCustomer.client_type === 'pro' ? <Buildings size={24} weight="fill" /> : <User size={24} weight="fill" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-lg">{selectedCustomer.name || 'Client'}</h3>
                        <Badge variant={selectedCustomer.client_type === 'pro' ? 'default' : 'secondary'} className={`text-[10px] ${selectedCustomer.client_type === 'pro' ? 'bg-blue-600' : 'bg-rose-600 text-white'}`}>
                          {selectedCustomer.client_type === 'pro' ? 'PRO' : 'PARTICULIER'}
                        </Badge>
                      </div>
                      {selectedCustomer.company_name && (
                        <p className="text-sm text-slate-600 flex items-center gap-1 mt-0.5">
                          <Buildings size={13} /> {selectedCustomer.company_name}
                          {selectedCustomer.siret && <span className="text-slate-400 text-xs ml-2">SIRET: {selectedCustomer.siret}</span>}
                        </p>
                      )}
                      <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                        <Envelope size={13} /> {selectedCustomer.email}
                      </p>
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <MapPin size={13} /> {selectedCustomer.city} ({selectedCustomer.postal_code})
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setSelectedCustomer(null)}>
                    <X size={18} />
                  </Button>
                </div>

                <Separator className="my-3" />

                <div className="grid grid-cols-6 gap-3 text-center">
                  <div>
                    <p className="text-xl font-bold">{selectedCustomer.total_orders}</p>
                    <p className="text-[11px] text-slate-500">Commandes</p>
                  </div>
                  <div>
                    <p className="text-xl font-bold">{selectedCustomer.total_spent.toFixed(0)} EUR</p>
                    <p className="text-[11px] text-slate-500">Total depense</p>
                  </div>
                  <div data-testid="avg-basket">
                    <p className="text-xl font-bold">{selectedCustomer.avg_basket?.toFixed(0) || '0'} EUR</p>
                    <p className="text-[11px] text-slate-500">Panier moyen</p>
                  </div>
                  <div data-testid="frequency">
                    <p className="text-sm font-bold text-slate-700">{formatFrequency(selectedCustomer.frequency_days)}</p>
                    <p className="text-[11px] text-slate-500">Frequence</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-700">{selectedCustomer.zone_name}</p>
                    <p className="text-[11px] text-slate-500">Zone</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-700">
                      {selectedCustomer.last_order ? new Date(selectedCustomer.last_order).toLocaleDateString('fr-FR') : '-'}
                    </p>
                    <p className="text-[11px] text-slate-500">Derniere commande</p>
                  </div>
                </div>

                {/* Product Trends */}
                {loadingTrends ? (
                  <div className="flex items-center gap-2 mt-3 py-3 justify-center text-slate-400 text-sm">
                    <CircleNotch size={16} className="animate-spin" /> Chargement des tendances...
                  </div>
                ) : trends && trends.products?.length > 0 ? (
                  <div className="mt-4" data-testid="product-trends">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-bold text-slate-600 uppercase tracking-wider">Produits achetes (classement)</p>
                      <p className="text-[10px] text-slate-400">
                        {formatShortDate(trends.summary?.first_order)} - {formatShortDate(trends.summary?.last_order)}
                      </p>
                    </div>
                    <div className="space-y-1.5 max-h-[200px] overflow-y-auto pr-1">
                      {trends.products.map(p => {
                        const maxQty = trends.products[0]?.total_qty || 1;
                        const barWidth = Math.max(8, (p.total_qty / maxQty) * 100);
                        return (
                          <div key={p.product_id} className="flex items-center gap-2.5 p-2 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors" data-testid={`trend-product-${p.rank}`}>
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${p.rank <= 3 ? 'bg-amber-100 text-amber-700' : 'bg-slate-200 text-slate-500'}`}>
                              {p.rank}
                            </span>
                            {p.image ? (
                              <img src={p.image} alt="" className="w-8 h-8 rounded object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-8 h-8 rounded bg-slate-200 flex items-center justify-center flex-shrink-0">
                                <Package size={14} className="text-slate-400" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-[12px] font-medium truncate">{p.name}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                  <div className="h-full rounded-full" style={{ width: `${barWidth}%`, backgroundColor: p.rank <= 3 ? '#c2185b' : '#94a3b8' }} />
                                </div>
                              </div>
                              <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-400">
                                <span>{formatShortDate(p.first_purchase)} - {formatShortDate(p.last_purchase)}</span>
                              </div>
                            </div>
                            <div className="text-right flex-shrink-0">
                              <p className="text-sm font-bold">{p.total_qty}</p>
                              <p className="text-[10px] text-slate-400">{p.total_revenue.toFixed(0)} EUR</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : trends && trends.products?.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center mt-3">Aucun produit achete</p>
                ) : null}

                <div className="mt-3 flex gap-2">
                  <Button size="sm" className="bg-[#0052FF] text-xs" onClick={() => openProfile(selectedCustomer.email)} data-testid="edit-profile-btn">
                    Modifier le profil
                  </Button>
                  <Button size="sm" variant="outline" className="text-xs"
                    onClick={() => window.open(`/admin/customers/${encodeURIComponent(selectedCustomer.email)}`, '_blank')}>
                    Voir fiche complete <CaretRight size={12} className="ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* ── Profile Edit Dialog ── */}
      <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Profil client</DialogTitle>
          </DialogHeader>
          {customerProfile && (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Type de client</Label>
                <Select value={customerProfile.client_type} onValueChange={v => setCustomerProfile(p => ({ ...p, client_type: v }))}>
                  <SelectTrigger data-testid="profile-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="particulier">Particulier</SelectItem>
                    <SelectItem value="pro">Professionnel</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {customerProfile.client_type === 'pro' && (
                <>
                  <Separator />
                  <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Informations entreprise</p>
                  <div className="space-y-1.5">
                    <Label>Raison sociale</Label>
                    <Input value={customerProfile.company_name || ''} onChange={e => setCustomerProfile(p => ({ ...p, company_name: e.target.value }))} placeholder="Ex: Restaurant Le Provencal" data-testid="profile-company" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label>SIRET</Label>
                      <Input value={customerProfile.siret || ''} onChange={e => setCustomerProfile(p => ({ ...p, siret: e.target.value }))} placeholder="123 456 789 00012" data-testid="profile-siret" />
                    </div>
                    <div className="space-y-1.5">
                      <Label>TVA Intra.</Label>
                      <Input value={customerProfile.tva_intra || ''} onChange={e => setCustomerProfile(p => ({ ...p, tva_intra: e.target.value }))} placeholder="FR12345678901" data-testid="profile-tva" />
                    </div>
                  </div>
                  <Separator />
                  <p className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Adresse de facturation</p>
                  <div className="space-y-2">
                    <Input value={customerProfile.billing_address?.address || ''} onChange={e => setCustomerProfile(p => ({ ...p, billing_address: { ...p.billing_address, address: e.target.value } }))} placeholder="Adresse" />
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={customerProfile.billing_address?.postal_code || ''} onChange={e => setCustomerProfile(p => ({ ...p, billing_address: { ...p.billing_address, postal_code: e.target.value } }))} placeholder="Code postal" />
                      <Input value={customerProfile.billing_address?.city || ''} onChange={e => setCustomerProfile(p => ({ ...p, billing_address: { ...p.billing_address, city: e.target.value } }))} placeholder="Ville" />
                    </div>
                  </div>
                </>
              )}

              <div className="space-y-1.5">
                <Label>Notes internes</Label>
                <Input value={customerProfile.notes || ''} onChange={e => setCustomerProfile(p => ({ ...p, notes: e.target.value }))} placeholder="Notes visibles uniquement par l'admin" />
              </div>

              <Button onClick={saveProfile} disabled={savingProfile} className="w-full bg-[#0052FF]" data-testid="save-profile-btn">
                {savingProfile ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DeliveryMapPage;
